import { useState } from "react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/admin/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { Lock, Mail, Shield, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (pw.new_password.length < 10) {
      setError("New password must be at least 10 characters.");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setSuccess(true);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated successfully.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-settings-page" className="p-6 lg:p-10 max-w-3xl">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your account.</p>
      </div>

      {/* Account info */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-4">
          <Shield className="h-4 w-4 text-sky-600" /> Account
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Name</p>
            <p className="mt-1 text-sm font-medium text-slate-800">{user?.name}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email</p>
            <p className="mt-1 text-sm font-medium text-slate-800 flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-slate-400" /> {user?.email}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Role</p>
            <p className="mt-1 text-sm font-medium text-slate-800 capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1">
          <Lock className="h-4 w-4 text-sky-600" /> Change password
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Use a strong password - at least 10 characters. Defaults seeded by the server should be rotated before launch.
        </p>

        {error && (
          <div data-testid="settings-pw-error" className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2.5">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div data-testid="settings-pw-success" className="mb-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg px-3 py-2.5">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Password updated. You'll stay signed in.</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="current_password" className="text-sm font-medium text-slate-700">Current password</Label>
            <Input
              id="current_password"
              type="password"
              data-testid="settings-current-password"
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              required
              className="mt-1.5 h-11 border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="new_password" className="text-sm font-medium text-slate-700">New password</Label>
            <Input
              id="new_password"
              type="password"
              data-testid="settings-new-password"
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              required
              minLength={10}
              className="mt-1.5 h-11 border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-sm font-medium text-slate-700">Confirm new password</Label>
            <Input
              id="confirm"
              type="password"
              data-testid="settings-confirm-password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              required
              className="mt-1.5 h-11 border-slate-300"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="settings-pw-submit"
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg h-11 text-sm font-semibold px-6 mt-2"
          >
            {loading ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
